import React, { useState, useEffect } from 'react';
import { HelpCircle, ChevronDown, Search } from 'lucide-react';
import { Footer } from '../Footer';
import { PublicHeader } from '../PublicHeader';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export function FAQ() {
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs: FAQItem[] = [
    {
      category: 'General',
      question: 'What is BidWin?',
      answer: 'BidWin is an AI-powered platform that helps businesses find, analyze, and win government contracts. Our platform uses advanced algorithms to match your company with relevant opportunities and assists in proposal generation.'
    },
    {
      category: 'General',
      question: 'How do I get started with BidWin?',
      answer: 'Getting started is easy! Simply create a free account, complete your company profile with relevant information like NAICS codes and past performance, and start exploring contract opportunities. Our AI will automatically start matching you with relevant contracts.'
    },
    {
      category: 'Subscriptions',
      question: 'What subscription plans are available?',
      answer: 'We offer several plans: Free (basic access), Growth (AI-powered features), Pro (advanced features), and Enterprise (custom solutions). Each plan includes different levels of access to our AI tools and contract database.'
    },
    {
      category: 'AI Features',
      question: 'How does the AI proposal generation work?',
      answer: 'Our AI analyzes your company profile, past performance, and the contract requirements to generate tailored proposal content. The AI considers factors like technical capabilities, past success, and compliance requirements to create compelling proposals.'
    },
    {
      category: 'Technical',
      question: 'Is my data secure?',
      answer: 'Yes, we take data security seriously. All data is encrypted in transit and at rest, and we follow industry best practices for security. We never share your sensitive information with third parties.'
    }
  ];

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-bw-app">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-bw-sidebar rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>
            
            <div className="relative text-center text-white">
              <div className="flex items-center justify-center mb-6">
                <HelpCircle className="h-12 w-12 text-bw-accent" />
              </div>
              <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
              <p className="text-white/90 max-w-2xl mx-auto">
                Find answers to common questions about BidWin's features, pricing, and capabilities.
              </p>
              
              <div className="mt-8 max-w-xl mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-servet-red focus:border-transparent"
                  />
                  <Search className="absolute left-4 top-3.5 h-5 w-5 text-white/60" />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {categories.map(category => (
              <div key={category} className="bg-bw-surface rounded-xl shadow-lg overflow-hidden">
                <h2 className="text-lg font-semibold text-bw-ink p-6 border-b">
                  {category}
                </h2>
                <div className="divide-y">
                  {filteredFaqs
                    .filter(faq => faq.category === category)
                    .map((faq, index) => (
                      <div key={index} className="p-6">
                        <button
                          onClick={() => setActiveItem(activeItem === faq.question ? null : faq.question)}
                          className="w-full flex justify-between items-start text-left"
                        >
                          <span className="text-bw-ink font-medium">{faq.question}</span>
                          <ChevronDown
                            className={`h-5 w-5 text-bw-muted transform transition-transform ${
                              activeItem === faq.question ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {activeItem === faq.question && (
                          <p className="mt-4 text-bw-muted">
                            {faq.answer}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-bw-surface rounded-lg shadow-lg p-8 text-center">
            <h3 className="text-lg font-medium text-bw-ink mb-2">
              Can't find what you're looking for?
            </h3>
            <p className="text-bw-muted mb-4">
              Our support team is here to help with any questions you may have.
            </p>
            <a
              href="mailto:support@servetdigital.com"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-bw-primary hover:bg-bw-primary/90"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}